'use client'

import { useCallback, useEffect, useState } from 'react'
import { Card } from '@/components/ui/card'
import { EXAM_RANGES, type ExamType } from '@/lib/scores/official-score'

interface ScoreReport {
  id: string
  total: number
  rw: number | null
  math: number | null
  examType: string
  testDate: string | null
  source: string
  verified: boolean
  createdAt: string
}

function fmtDate(iso: string | null): string {
  if (!iso) return ''
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  return d.toLocaleDateString(undefined, { month: 'short', year: 'numeric' })
}

/** Small badge reflecting whether a score has a verified proof attached. */
function ProofBadge({ rep }: { rep: Pick<ScoreReport, 'source' | 'verified'> }) {
  if (rep.verified) {
    return (
      <span className="ml-1 rounded bg-emerald-500/15 px-1.5 py-0.5 text-xs font-medium text-emerald-700 dark:text-emerald-300">
        ✓ Verified
      </span>
    )
  }
  if (rep.source === 'UPLOADED') {
    return (
      <span className="ml-1 rounded bg-amber-500/15 px-1.5 py-0.5 text-xs font-medium text-amber-700 dark:text-amber-300">
        Pending review
      </span>
    )
  }
  return null
}

/**
 * Self-contained "enter your official College Board score" island (roadmap 2.3).
 * Fetches + posts /api/score-report. Rendered on the dashboard (compact prompt)
 * and on the guarantee page. The browser's score entry feeds the score
 * guarantee + the measured-retake loop.
 */
export default function OfficialScoreEntry({ heading }: { heading?: string }) {
  const [reports, setReports] = useState<ScoreReport[]>([])
  const [loading, setLoading] = useState(true)
  const [showForm, setShowForm] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const [examType, setExamType] = useState<ExamType>('SAT')
  const [total, setTotal] = useState('')
  const [rw, setRw] = useState('')
  const [math, setMath] = useState('')
  const [testDate, setTestDate] = useState('')
  // Optional proof document. Self-reported entries track progress; an UPLOADED
  // one is the only kind that can back a guarantee claim.
  const [file, setFile] = useState<File | null>(null)

  const load = useCallback(async () => {
    try {
      const res = await fetch('/api/score-report', { cache: 'no-store' })
      if (!res.ok) throw new Error()
      const data = await res.json()
      setReports(Array.isArray(data.reports) ? data.reports : [])
    } catch {
      // Non-fatal — the form still works.
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    load()
  }, [load])

  function resetForm() {
    setTotal('')
    setRw('')
    setMath('')
    setTestDate('')
    setExamType('SAT')
    setFile(null)
    setError(null)
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault()
    setSaving(true)
    setError(null)
    try {
      // With a proof file → multipart upload endpoint (UPLOADED + claimable).
      // Without → the plain JSON entry (self-reported, progress-tracking only).
      let res: Response
      if (file) {
        const fd = new FormData()
        fd.set('file', file)
        fd.set('examType', examType)
        fd.set('total', total)
        if (rw.trim() !== '') fd.set('rw', rw)
        if (math.trim() !== '') fd.set('math', math)
        if (testDate.trim() !== '') fd.set('testDate', testDate)
        res = await fetch('/api/score-report/upload', { method: 'POST', body: fd })
      } else {
        res = await fetch('/api/score-report', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            examType,
            total: Number(total),
            rw: rw.trim() === '' ? null : Number(rw),
            math: math.trim() === '' ? null : Number(math),
            testDate: testDate.trim() === '' ? null : testDate,
          }),
        })
      }
      const data = await res.json().catch(() => ({}))
      if (!res.ok) {
        setError(data.error ?? 'Could not save your score. Please check the values.')
        return
      }
      resetForm()
      setShowForm(false)
      await load()
    } catch {
      setError('Could not save your score. Please try again.')
    } finally {
      setSaving(false)
    }
  }

  async function remove(id: string) {
    setReports((prev) => prev.filter((r) => r.id !== id))
    await fetch(`/api/score-report?id=${encodeURIComponent(id)}`, { method: 'DELETE' }).catch(
      () => {},
    )
  }

  if (loading) return null

  const latest = reports[0] ?? null
  const r = EXAM_RANGES[examType]

  return (
    <Card className="p-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="min-w-0">
          <h3 className="text-sm font-semibold text-foreground">
            {heading ?? 'Your official SAT score'}
          </h3>
          {latest ? (
            <p className="mt-0.5 text-sm text-muted-foreground">
              Latest:{' '}
              <span className="font-semibold text-foreground">
                {latest.total}
                {latest.rw != null && latest.math != null
                  ? ` (${latest.rw} RW · ${latest.math} Math)`
                  : ''}
              </span>
              {latest.testDate ? ` · ${fmtDate(latest.testDate)}` : ''} · {latest.examType}
              <ProofBadge rep={latest} />
            </p>
          ) : (
            <p className="mt-0.5 text-sm text-muted-foreground">
              Took the real exam? Add your official score to track your progress.
            </p>
          )}
        </div>
        {!showForm && (
          <button
            type="button"
            onClick={() => {
              resetForm()
              setShowForm(true)
            }}
            className="inline-flex h-9 shrink-0 items-center justify-center rounded-md border border-brand/50 px-4 text-sm font-medium text-brand-ink transition hover:bg-brand/10 dark:text-brand-ink"
          >
            {latest ? 'Add another' : 'Add my score'}
          </button>
        )}
      </div>

      {/* Existing entries (when more than one, list them with a remove control). */}
      {reports.length > 1 && !showForm && (
        <ul className="mt-3 space-y-1 border-t border-border pt-3 text-sm">
          {reports.map((rep) => (
            <li key={rep.id} className="flex items-center justify-between gap-2">
              <span className="text-muted-foreground">
                {rep.examType} <span className="font-medium text-foreground">{rep.total}</span>
                {rep.testDate ? ` · ${fmtDate(rep.testDate)}` : ''}
                <ProofBadge rep={rep} />
              </span>
              <button
                type="button"
                onClick={() => remove(rep.id)}
                className="text-xs text-muted-foreground underline-offset-2 hover:text-foreground hover:underline"
              >
                Remove
              </button>
            </li>
          ))}
        </ul>
      )}

      {showForm && (
        <form onSubmit={submit} className="mt-3 space-y-3 border-t border-border pt-3">
          {error && <p className="text-sm text-red-500">{error}</p>}
          <div className="flex flex-wrap gap-3">
            <label className="text-sm">
              <span className="mb-1 block text-xs text-muted-foreground">Exam</span>
              <select
                value={examType}
                onChange={(e) => setExamType(e.target.value as ExamType)}
                className="h-9 rounded-md border border-border bg-background px-2 text-sm text-foreground"
              >
                <option value="SAT">SAT</option>
                <option value="PSAT">PSAT</option>
              </select>
            </label>
            <label className="text-sm">
              <span className="mb-1 block text-xs text-muted-foreground">
                Total ({r.totalMin}–{r.totalMax}) *
              </span>
              <input
                type="number"
                inputMode="numeric"
                required
                step={10}
                min={r.totalMin}
                max={r.totalMax}
                value={total}
                onChange={(e) => setTotal(e.target.value)}
                className="h-9 w-24 rounded-md border border-border bg-background px-2 text-sm text-foreground"
              />
            </label>
            <label className="text-sm">
              <span className="mb-1 block text-xs text-muted-foreground">R&amp;W</span>
              <input
                type="number"
                inputMode="numeric"
                step={10}
                min={r.secMin}
                max={r.secMax}
                value={rw}
                onChange={(e) => setRw(e.target.value)}
                className="h-9 w-20 rounded-md border border-border bg-background px-2 text-sm text-foreground"
              />
            </label>
            <label className="text-sm">
              <span className="mb-1 block text-xs text-muted-foreground">Math</span>
              <input
                type="number"
                inputMode="numeric"
                step={10}
                min={r.secMin}
                max={r.secMax}
                value={math}
                onChange={(e) => setMath(e.target.value)}
                className="h-9 w-20 rounded-md border border-border bg-background px-2 text-sm text-foreground"
              />
            </label>
            <label className="text-sm">
              <span className="mb-1 block text-xs text-muted-foreground">Test date</span>
              <input
                type="date"
                value={testDate}
                onChange={(e) => setTestDate(e.target.value)}
                className="h-9 rounded-md border border-border bg-background px-2 text-sm text-foreground"
              />
            </label>
          </div>
          <p className="text-xs text-muted-foreground">
            Sections are optional. If you enter both, they must add up to your total.
          </p>

          {/* Proof upload — optional for tracking, REQUIRED to claim the score
              guarantee. A typed score alone can't trigger a free month. */}
          <div className="rounded-md border border-dashed border-border p-3">
            <label className="block text-sm font-medium text-foreground">
              Score report{' '}
              <span className="font-normal text-muted-foreground">
                (optional — required to claim the guarantee)
              </span>
            </label>
            <input
              type="file"
              accept="image/png,image/jpeg,image/webp,application/pdf"
              onChange={(e) => setFile(e.target.files?.[0] ?? null)}
              className="mt-2 block w-full text-sm text-muted-foreground file:mr-3 file:rounded-md file:border-0 file:bg-muted file:px-3 file:py-1.5 file:text-sm file:font-medium file:text-foreground hover:file:bg-accent"
            />
            <p className="mt-1 text-xs text-muted-foreground">
              A screenshot or PDF of your official College Board score (max 4 MB). Kept private —
              only you and our team can see it.
            </p>
          </div>

          <div className="flex gap-2">
            <button
              type="submit"
              disabled={saving || total.trim() === ''}
              // Disabled state: drop the gradient + white text (which fade into
              // an unreadable smear) for a flat, theme-aware muted button so the
              // "Save score" label stays legible in both light and dark mode.
              className="inline-flex h-9 items-center justify-center rounded-md bg-brand px-4 text-sm font-semibold text-on-brand transition disabled:cursor-not-allowed disabled:bg-none disabled:bg-muted disabled:text-muted-foreground"
            >
              {saving ? 'Saving…' : 'Save score'}
            </button>
            <button
              type="button"
              onClick={() => {
                setShowForm(false)
                resetForm()
              }}
              className="inline-flex h-9 items-center justify-center rounded-md border border-border px-4 text-sm text-muted-foreground hover:text-foreground"
            >
              Cancel
            </button>
          </div>
        </form>
      )}
    </Card>
  )
}
